<nav class="course-content-sidebar" aria-label="Course lessons">
  <!-- Course Info -->
  <div class="course-info">
    <h2 class="course-name">{{ courseTrack?.name }}</h2>

    <!-- Course Badges -->
    <div class="course-badges">
      <ut-academy-badge
        type="lessons"
        [value]="totalLessons">
      </ut-academy-badge>

      @if (totalEstimatedTime) {
        <ut-academy-badge
          type="time"
          [value]="totalEstimatedTime">
        </ut-academy-badge>
      }

      @if (difficultyLevel) {
        <ut-academy-badge
          type="difficulty"
          [value]="difficultyLevel">
        </ut-academy-badge>
      }
    </div>
  </div>

  <!-- Lesson List -->
  <ol class="lesson-list" role="list">
    @for (lesson of lessons; track lesson; let i = $index) {
      <li role="none">
        <button
          type="button"
          class="lesson-item"
          [class.lesson-current]="lesson.isCurrentLesson"
          [class.lesson-clickable]="isLessonClickable(lesson)"
          [class.lesson-completed]="lesson.status === 'completed'"
          [class.lesson-in-progress]="lesson.status === 'in-progress'"
          [class.lesson-available]="lesson.status === 'available'"
          [class.lesson-failed]="lesson.status === 'failed'"
          (click)="onLessonClick(lesson)"
          [attr.aria-label]="getLessonAriaLabel(lesson, i)"
          [attr.aria-current]="lesson.isCurrentLesson ? 'step' : null">
          <!-- aria-hidden wrapper: prevents JAWS browse mode from traversing into
          button children after reading the aria-label on the button itself -->
          <span aria-hidden="true">
            <ut-lesson-status-icon
              [status]="lesson.status"
              [lessonNumber]="getLessonNumber(i)"
              size="medium">
            </ut-lesson-status-icon>
            <div class="lesson-info">
              <span class="lesson-name">{{ lesson.title }}</span>
              <div class="lesson-badges">
                @if (lesson.is_new) {
                  <ut-academy-badge
                    type="new"
                    size="small">
                  </ut-academy-badge>
                }
                @if (lesson.is_updated && !lesson.is_new) {
                  <ut-academy-badge
                    type="updated"
                    size="small">
                  </ut-academy-badge>
                }
                @if (lesson.status === 'failed') {
                  <ut-academy-badge
                    type="failed"
                    size="small">
                  </ut-academy-badge>
                }
              </div>
            </div>
          </span>
        </button>
      </li>
    }
  </ol>

  <!-- Quiz Section -->
  @if (hasQuiz) {
    <div class="quiz-section">
      <button
        type="button"
        class="quiz-item"
        [class.quiz-locked]="quizStatus === 'locked'"
        [class.quiz-available]="quizStatus === 'available'"
        [class.quiz-clickable]="isQuizAvailable && !quizActive"
        [class.quiz-passed]="quizStatus === 'passed'"
        [class.quiz-failed]="quizStatus === 'failed'"
        [attr.aria-disabled]="(!isQuizAvailable && quizStatus !== 'passed') ? 'true' : null"
        (click)="onQuizClick()">
        <div class="quiz-icon-container">
          <!-- Lock icon for locked state -->
          @if (quizStatus === 'locked') {
            <svg
              class="quiz-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <path d="M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
          <!-- X-circle icon for failed state -->
          @if (quizStatus === 'failed') {
            <svg
              class="quiz-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <path d="M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
          <!-- Graduation cap icon for available state -->
          @if (quizStatus === 'available') {
            <svg
              class="quiz-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <path d="M5 10.0001V16.0112C5 16.3702 5 16.5496 5.05465 16.7081C5.10299 16.8482 5.18187 16.9759 5.28558 17.0817C5.40287 17.2015 5.5634 17.2818 5.88446 17.4423L11.2845 20.1423C11.5468 20.2735 11.678 20.3391 11.8156 20.3649C11.9375 20.3877 12.0625 20.3877 12.1844 20.3649C12.322 20.3391 12.4532 20.2735 12.7155 20.1423L18.1155 17.4423C18.4366 17.2818 18.5971 17.2015 18.7144 17.0817C18.8181 16.9759 18.897 16.8482 18.9453 16.7081C19 16.5496 19 16.3702 19 16.0112V10.0001M2 8.50006L11.6422 3.67895C11.7734 3.61336 11.839 3.58056 11.9078 3.56766C11.9687 3.55622 12.0313 3.55622 12.0922 3.56766C12.161 3.58056 12.2266 3.61336 12.3578 3.67895L22 8.50006L12.3578 13.3212C12.2266 13.3868 12.161 13.4196 12.0922 13.4325C12.0313 13.4439 11.9687 13.4439 11.9078 13.4325C11.839 13.4196 11.7734 13.3868 11.6422 13.3212L2 8.50006Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
          <!-- Checkmark circle icon for passed state -->
          @if (quizStatus === 'passed') {
            <svg
              class="quiz-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true">
              <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
        </div>
        <div class="quiz-info">
          <span class="quiz-label">{{ quizLabel }}</span>
          <span class="quiz-hint">{{ quizHint }}</span>
        </div>
      </button>
    </div>
  }
</nav>

<!-- Practice Test Cycle Card -->
@if (cycleStatus) {
  <div class="cycle-card" role="region" [attr.aria-label]="'Practice Test Cycle: ' + cycleLabel">
    <div class="cycle-card__header">
      <span class="cycle-card__title">Practice Test Cycle:</span>
      <span class="cycle-card__course-name">{{ courseTrack?.name }}</span>
    </div>
    <div class="cycle-card__divider"></div>
    <div
      class="cycle-card__status"
      [class.cycle-card__status--locked]="cycleStatus === 'locked' || cycleStatus === 'locked-prerequisites'"
      [class.cycle-card__status--available]="cycleStatus === 'available'"
      [class.cycle-card__status--signed-up]="cycleStatus === 'signed-up' || cycleStatus === 'passed-participating'"
      [class.cycle-card__status--passed]="cycleStatus === 'passed' || cycleStatus === 'passed-can-retry'">
      <div class="cycle-card__icon-container">
        <!-- Lock icon for locked states -->
        @if (cycleStatus === 'locked' || cycleStatus === 'locked-prerequisites') {
          <svg
            class="cycle-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <path d="M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        }
        <!-- Graduation cap icon for available/in-progress states -->
        @if (cycleStatus === 'available' || cycleStatus === 'signed-up' || cycleStatus === 'passed-participating') {
          <svg
            class="cycle-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <path d="M5 10.0001V16.0112C5 16.3702 5 16.5496 5.05465 16.7081C5.10299 16.8482 5.18187 16.9759 5.28558 17.0817C5.40287 17.2015 5.5634 17.2818 5.88446 17.4423L11.2845 20.1423C11.5468 20.2735 11.678 20.3391 11.8156 20.3649C11.9375 20.3877 12.0625 20.3877 12.1844 20.3649C12.322 20.3391 12.4532 20.2735 12.7155 20.1423L18.1155 17.4423C18.4366 17.2818 18.5971 17.2015 18.7144 17.0817C18.8181 16.9759 18.897 16.8482 18.9453 16.7081C19 16.5496 19 16.3702 19 16.0112V10.0001M2 8.50006L11.6422 3.67895C11.7734 3.61336 11.839 3.58056 11.9078 3.56766C11.9687 3.55622 12.0313 3.55622 12.0922 3.56766C12.161 3.58056 12.2266 3.61336 12.3578 3.67895L22 8.50006L12.3578 13.3212C12.2266 13.3868 12.161 13.4196 12.0922 13.4325C12.0313 13.4439 11.9687 13.4439 11.9078 13.4325C11.839 13.4196 11.7734 13.3868 11.6422 13.3212L2 8.50006Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        }
        <!-- Checkmark circle icon for completed states -->
        @if (cycleStatus === 'passed' || cycleStatus === 'passed-can-retry') {
          <svg
            class="cycle-card__icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        }
      </div>
      <div class="cycle-card__info">
        <span class="cycle-card__label">{{ cycleLabel }}</span>
        <span class="cycle-card__hint">{{ cycleHint }}</span>
      </div>
    </div>
    @if (cycleStatus === 'passed-can-retry') {
      <div class="cycle-card__retry">
        <div class="cycle-card__divider"></div>
        <span class="cycle-card__retry-text">Want to do it again?</span>
        <span class="cycle-card__retry-hint">Practice testing in a live environment</span>
      </div>
    }
    @if (cycleStatus === 'available' || cycleStatus === 'passed-can-retry') {
      <button
        type="button"
        class="cycle-card__sign-up-btn"
        (click)="onCycleSignUp()">
        Sign Up
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M4.16663 10H15.8333M15.8333 10L9.99996 4.16669M15.8333 10L9.99996 15.8334" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    }
    @if ((cycleStatus === 'signed-up' || cycleStatus === 'passed-participating') && showSignUpConfirmation) {
      <div class="cycle-card__confirmation">
        <p class="cycle-card__confirmation-text">
          You are now signed up to the practice test cycle, and will be invited as soon as we find an available spot for you.
        </p>
        <p class="cycle-card__confirmation-text">
          Please be patient, as this might take a few days: we are limiting the number of spots in each cycle to ensure our team members are able to provide the right level of attention to all participants.
        </p>
        <button
          type="button"
          class="cycle-card__confirm-btn"
          (click)="onConfirmSignUp()">
          Got It
        </button>
      </div>
    }
    @if ((cycleStatus === 'signed-up' || cycleStatus === 'passed-participating') && !showSignUpConfirmation) {
      <ul class="cycle-card__tips">
        <li>Look for the test cycle invitation. Make sure to accept the cycle when it arrives.</li>
        <li>The Cycle might be Active or Pending. If Pending, you will be notified when it gets activated.</li>
        <li>Once Active, you will be able to communicate with the Testing Team Lead in the Chat. The TTL is there for you: reach out for clarifications or questions.</li>
        <li>Make sure to meet the cycle requirements before it closes.</li>
      </ul>
    }
    @if (cycleStatus === 'passed-participating') {
      <div class="cycle-card__reassurance">
        You've already passed a similar cycle, so you'll keep that achievement even if you don't pass this new one.
      </div>
    }
  </div>
}

<!-- Next Course button -->
@if (showNextCourse) {
  <button
    type="button"
    class="next-course-btn"
    (click)="onNextCourse()">
    Next Course
    <svg class="next-icon" width="17" height="13" viewBox="0 0 17 13" fill="none"
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16.1666 4.16634H4.91663C2.84556 4.16634 1.16663 5.84527 1.16663 7.91634C1.16663 9.98741 2.84556 11.6663 4.91663 11.6663H8.66663M16.1666 4.16634L12.8333 0.833008M16.1666 4.16634L12.8333 7.49967"
        stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </button>
}

<!-- Return to Course Catalog button -->
@if (showReturnToTracks) {
  <button
    type="button"
    class="return-to-tracks-btn"
    (click)="onReturnToTracks()">
    Return to Course Catalog
    <svg class="return-icon" width="17" height="13" viewBox="0 0 17 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M0.833374 4.16634H12.0834C14.1544 4.16634 15.8334 5.84527 15.8334 7.91634C15.8334 9.98741 14.1544 11.6663 12.0834 11.6663H8.33337M0.833374 4.16634L4.16671 0.833008M0.833374 4.16634L4.16671 7.49967" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </button>
}
